import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import MusicNoteOutlinedIcon from '@mui/icons-material/MusicNoteOutlined';
import { Trans, useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { format, Locale } from 'date-fns';
import { enGB, pl as plPL } from 'date-fns/locale';
import { PassTargetDetail, PassUsageEvent, PassUsageEventType, PassWalletDetail } from '../../models/Pass';
import {
  minutesToDisplayHours,
  passDaysRemaining,
  passQuantityLabel,
  passQuantityDeltaLabel,
  passUsedQuantity,
  passValidityStartPolicyLabel,
} from '../../utils/passFormatting';
import { fetchPassTargets } from '../../utils/passTargets';
import { PassAudience, passUsageBookingPath } from '../../utils/passUsageBookingPath';
import RoomBookingUserAvatar from '../RoomBookingUserAvatar';
import PassInstanceStatusChip from './PassInstanceStatusChip';
import PassTargetCollection from './PassTargetCollection';
import PassTargetTypeChip from './PassTargetTypeChip';
import SEO from '../SEO';
import { passDetailPageSx, PASS_BOARDING_STUB_WIDTH, PASS_IDENTITY_AVATAR_SIZE } from './passDetailLayout';

const dateLocales: { [key: string]: Locale } = {
  en: enGB,
  pl: plPL,
};

const eventTone = (eventType: PassUsageEventType): { color: string } => {
  switch (eventType) {
    case 'RESERVED':
      return { color: 'info.main' };
    case 'CONSUMED':
      return { color: 'success.main' };
    case 'RELEASED':
      return { color: 'text.secondary' };
    case 'MANUALLY_ADJUSTED':
      return { color: 'warning.main' };
    default:
      return { color: 'text.secondary' };
  }
};

export interface PassInstanceDetailsViewProps {
  audience: PassAudience;
  pass: PassWalletDetail | null;
  loading: boolean;
  error: string | null;
  /** Back chip label; click handler comes from the page (wallet list vs history). */
  backLabel: string;
  onBack: () => void;
}

/** Read-only pass instance detail (wallet or provider-scoped). Status/remaining come from the detail payload, not the event list. */
const PassInstanceDetailsView: React.FC<PassInstanceDetailsViewProps> = ({
  audience,
  pass,
  loading,
  error,
  backLabel,
  onBack,
}) => {
  const { t, i18n } = useTranslation();
  const dateLocale = dateLocales[i18n.language] || enGB;
  const [targets, setTargets] = useState<PassTargetDetail[]>([]);

  useEffect(() => {
    let cancelled = false;
    if (!pass) {
      setTargets([]);
      return;
    }
    void fetchPassTargets(pass.targetType, pass.targetIds ?? []).then((resolved) => {
      if (!cancelled) setTargets(resolved);
    });
    return () => {
      cancelled = true;
    };
  }, [pass]);

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return '—';
    try {
      return format(new Date(dateString), 'PP', { locale: dateLocale });
    } catch {
      return new Date(dateString).toLocaleDateString(i18n.language);
    }
  };

  const formatDateTime = (dateString: string) => {
    try {
      return format(new Date(dateString), 'PPp', { locale: dateLocale });
    } catch {
      return new Date(dateString).toLocaleString(i18n.language);
    }
  };

  const usageEventLabel = (eventType: PassUsageEvent['eventType']) =>
    t(`userPasses.details.eventType.${eventType}`, eventType);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !pass) {
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto', p: { xs: 2, sm: 4 } }}>
        <Alert severity="error">{error || t('userPasses.details.fetchError', 'Failed to load this pass')}</Alert>
      </Box>
    );
  }

  const quantityBreakdownValue = (quantity: number) =>
    pass.unitType === 'MINUTE' ? minutesToDisplayHours(quantity) : quantity;

  const buyer = pass.buyer;
  const buyerName = buyer?.name?.trim() || null;
  const buyerBandName = buyer?.bandName?.trim() || null;
  const usedQuantity = passUsedQuantity(pass.remainingQuantity, pass.totalQuantity);
  const usedRatio =
    pass.totalQuantity > 0 ? Math.min(1, Math.max(0, usedQuantity / pass.totalQuantity)) : 0;
  const usageEvents = pass.usageEvents ?? [];

  const validityNotStarted = !pass.validFrom && !pass.validUntil;
  const daysLeft = passDaysRemaining(pass.validUntil);
  const durationDays = pass.validityDurationDays;

  const metaItems: { label: string; value: string }[] = [
    { label: t('userPasses.details.purchasedAt', 'Purchased'), value: formatDate(pass.purchasedAt) },
  ];

  const validityStartLabel = passValidityStartPolicyLabel(pass.validityStartPolicy, t);
  if (validityStartLabel) {
    metaItems.push({
      label: t('userPasses.details.validityStarts', 'Validity starts'),
      value: validityStartLabel,
    });
  }

  if (validityNotStarted) {
    if (durationDays != null && durationDays > 0) {
      metaItems.push({
        label: t('userPasses.details.validity', 'Validity'),
        value: t('userPasses.details.validityDurationDays', '{{count}} days', {
          count: durationDays,
        }),
      });
    } else if (!validityStartLabel) {
      metaItems.push({
        label: t('userPasses.details.validity', 'Validity'),
        value: t('userPasses.details.validityStartsOnFirstUse', 'Starts on first use'),
      });
    }
  } else {
    metaItems.push({
      label: t('userPasses.details.validUntil', 'Valid until'),
      value: formatDate(pass.validUntil),
    });
    if (daysLeft != null && daysLeft >= 0) {
      metaItems.push({
        label: t('userPasses.details.expiresIn', 'Expires in'),
        value:
          daysLeft === 0
            ? t('userPasses.details.expiresInToday', 'Today')
            : t('userPasses.details.expiresInDays', '{{count}} days', { count: daysLeft }),
      });
    }
  }

  const breakdownItems = [
    {
      label: t('userPasses.details.reserved', 'Reserved'),
      value: quantityBreakdownValue(pass.reservedQuantity),
    },
    {
      label: t('userPasses.details.consumed', 'Consumed'),
      value: quantityBreakdownValue(pass.consumedQuantity),
    },
  ];

  return (
    <>
      <SEO title={pass.title} noindex />
      <Box sx={passDetailPageSx}>
        <Chip
          label={`← ${backLabel}`}
          onClick={onBack}
          sx={{
            mb: 2.5,
            bgcolor: 'transparent',
            color: 'text.primary',
            border: '1px solid',
            borderColor: 'divider',
            '&:hover': { bgcolor: 'transparent' },
          }}
        />

        {/* Landscape boarding-pass surface */}
        <Box
          sx={{
            position: 'relative',
            mb: 4,
            width: '100%',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'stretch',
            borderRadius: 3,
            overflow: 'hidden',
            bgcolor: 'rgba(255,255,255,0.03)',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 18px 48px rgba(0,0,0,0.35)',
            backgroundImage:
              'radial-gradient(ellipse 70% 80% at 0% 50%, rgba(78,103,235,0.16), transparent 55%), radial-gradient(ellipse 50% 70% at 100% 50%, rgba(255,78,113,0.07), transparent 50%)',
            minHeight: { md: 280 },
          }}
        >
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            sx={{
              position: 'absolute',
              top: { xs: 12, sm: 16 },
              right: { xs: 12, sm: 16 },
              zIndex: 2,
            }}
          >
            <PassTargetTypeChip targetType={pass.targetType} />
            <PassInstanceStatusChip status={pass.status} />
          </Stack>
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: 5,
              bgcolor: 'primary.main',
              display: { xs: 'none', md: 'block' },
            }}
          />

          {/* Main panel */}
          <Box
            sx={{
              flex: '1 1 auto',
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              pl: { xs: 2.5, sm: 3, md: 3.5 },
              pr: { xs: 2.5, sm: 3 },
              pt: { xs: 2.5, sm: 3 },
              pb: { xs: 2, md: 3 },
            }}
          >
            {audience !== 'user' && buyer && (
              <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 1.75, minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0, pt: 0.35 }}>
                  {t('userPasses.details.boughtBy', 'Bought by')}
                </Typography>
                <Box sx={{ pt: 0.1 }}>
                  <RoomBookingUserAvatar
                    userProfileImageUrl={buyer.profileImageUrl}
                    displayName={buyerName || ''}
                    size={PASS_IDENTITY_AVATAR_SIZE}
                  />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={700} noWrap>
                    {buyerName || t('userPasses.details.unknownBuyer', 'Pass owner')}
                  </Typography>
                  {buyerBandName && (
                    <Stack direction="row" spacing={0.4} alignItems="center" useFlexGap sx={{ mt: 0.15, minWidth: 0 }}>
                      <MusicNoteOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary', flexShrink: 0 }} />
                      <Typography variant="caption" color="text.secondary" component="span" noWrap>
                        <Trans
                          i18nKey="userPasses.details.playsAt"
                          defaults="Plays at <bandName>{{name}}</bandName>"
                          values={{ name: buyerBandName }}
                          components={{
                            bandName: (
                              <Typography
                                component="span"
                                variant="caption"
                                color="text.secondary"
                                fontWeight={600}
                              />
                            ),
                          }}
                        />
                      </Typography>
                    </Stack>
                  )}
                </Box>
              </Stack>
            )}

            <Stack spacing={1} sx={{ mb: 1, minWidth: 0, pr: { xs: 14, sm: 16 } }}>
              <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ minWidth: 0 }}>
                <ConfirmationNumberIcon color="primary" sx={{ fontSize: 26, flexShrink: 0, mt: 0.35 }} />
                <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    variant="h4"
                    fontWeight={800}
                    sx={{ lineHeight: 1.15, letterSpacing: '-0.02em', fontSize: { xs: '1.4rem', sm: '1.65rem' } }}
                  >
                    {pass.title}
                  </Typography>
                  {pass.organizationName?.trim() ? (
                    <Typography variant="body2" color="text.secondary">
                      {audience === 'user'
                        ? t('userPasses.purchasedFromOrganization', 'Purchased from {{name}}', {
                            name: pass.organizationName.trim(),
                          })
                        : t('userPasses.fromOrganization', 'From {{name}}', {
                            name: pass.organizationName.trim(),
                          })}
                    </Typography>
                  ) : null}
                </Stack>
              </Stack>
            </Stack>

            <Box sx={{ mt: 0.5, mb: 0 }}>
              <PassTargetCollection
                targetType={pass.targetType}
                targetIds={pass.targetIds ?? []}
                targets={targets}
                audience={audience}
              />
            </Box>

            <Box sx={{ flex: 1 }} />

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: `repeat(${Math.max(metaItems.length, 1)}, minmax(0, 1fr))`,
                gap: { xs: 1, sm: 1.75 },
                pt: 2,
                mt: 'auto',
              }}
            >
              {metaItems.map((item) => (
                <Box key={item.label} sx={{ minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: 'block',
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      mb: 0.25,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {item.label}
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {item.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Vertical perforation (landscape tear) */}
          <Box
            sx={{
              position: 'relative',
              flexShrink: 0,
              width: { xs: 'auto', md: 28 },
              height: { xs: 28, md: 'auto' },
              alignSelf: 'stretch',
              '&::before, &::after': {
                content: '""',
                position: 'absolute',
                width: 18,
                height: 18,
                borderRadius: '50%',
                bgcolor: 'background.default',
                border: '1px solid',
                borderColor: 'divider',
                zIndex: 1,
              },
              // Mobile: horizontal tear notches
              '@media (max-width: 899.95px)': {
                mx: 2,
                '&::before, &::after': {
                  top: '50%',
                  transform: 'translateY(-50%)',
                },
                '&::before': { left: -19 },
                '&::after': { right: -19 },
              },
              // Desktop: vertical tear notches
              '@media (min-width: 900px)': {
                my: 0,
                '&::before, &::after': {
                  left: '50%',
                  transform: 'translateX(-50%)',
                },
                '&::before': { top: -10 },
                '&::after': { bottom: -10 },
              },
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                borderColor: 'divider',
                // Mobile: dashed horizontal
                top: { xs: '50%', md: 12 },
                bottom: { xs: 'auto', md: 12 },
                left: { xs: 8, md: '50%' },
                right: { xs: 8, md: 'auto' },
                height: { xs: 0, md: 'auto' },
                width: { xs: 'auto', md: 0 },
                borderTop: { xs: '2px dashed', md: 'none' },
                borderLeft: { xs: 'none', md: '2px dashed' },
                transform: { xs: 'translateY(-50%)', md: 'translateX(-50%)' },
              }}
            />
          </Box>

          {/* Balance stub — right wing on landscape */}
          <Box
            sx={{
              flex: { xs: '0 0 auto', md: `0 0 ${PASS_BOARDING_STUB_WIDTH}px` },
              width: { md: PASS_BOARDING_STUB_WIDTH },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              px: { xs: 2.5, sm: 3 },
              pt: { xs: 2.5, md: 5 },
              pb: { xs: 2.5, md: 3 },
              bgcolor: { md: 'rgba(0,0,0,0.18)' },
            }}
          >
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ letterSpacing: '0.12em', display: 'block', mb: 0.5 }}
            >
              {t('userPasses.details.used', 'Used')}
            </Typography>
            <Typography
              variant="h4"
              fontWeight={800}
              sx={{ letterSpacing: '-0.03em', fontSize: { xs: '1.5rem', md: '1.65rem' }, mb: 1.25, lineHeight: 1.2 }}
            >
              {passQuantityLabel(usedQuantity, pass.totalQuantity, pass.unitType, t)}
            </Typography>

            <LinearProgress
              variant="determinate"
              value={usedRatio * 100}
              sx={{
                height: 8,
                borderRadius: 99,
                mb: 2,
                bgcolor: 'rgba(255,255,255,0.08)',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 99,
                  bgcolor:
                    usedRatio >= 0.85
                      ? 'error.main'
                      : usedRatio >= 0.65
                        ? 'warning.main'
                        : 'primary.main',
                },
              }}
            />

            <Stack spacing={1}>
              {breakdownItems.map((item) => (
                <Stack
                  key={item.label}
                  direction="row"
                  alignItems="baseline"
                  justifyContent="space-between"
                  spacing={1}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ letterSpacing: '0.06em', textTransform: 'uppercase' }}
                  >
                    {item.label}
                  </Typography>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {item.value}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        </Box>

        {/* Usage timeline */}
        <Box>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 2, letterSpacing: '-0.01em' }}>
            {t('userPasses.details.usageHistory', 'Usage history')}
          </Typography>

          {usageEvents.length === 0 ? (
            <Box
              sx={{
                py: 4,
                px: 2,
                textAlign: 'center',
                borderRadius: 2,
                border: '1px dashed',
                borderColor: 'divider',
                bgcolor: 'rgba(255,255,255,0.02)',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {t('userPasses.details.noUsageEvents', 'No usage yet')}
              </Typography>
            </Box>
          ) : (
            <Stack component="ol" spacing={0} sx={{ listStyle: 'none', m: 0, p: 0 }}>
              {usageEvents.map((event, index) => {
                const bookingHref = event.booking ? passUsageBookingPath(audience, event.booking) : null;
                const tone = eventTone(event.eventType);
                const isLast = index === usageEvents.length - 1;

                return (
                  <Box
                    component="li"
                    key={event.id}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '20px 1fr',
                      columnGap: 1.5,
                      alignItems: 'center',
                      position: 'relative',
                      pb: isLast ? 0 : 2.5,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        alignSelf: 'stretch',
                        position: 'relative',
                      }}
                    >
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          bgcolor: tone.color,
                          border: '2px solid',
                          borderColor: 'background.default',
                          boxShadow: (theme) => `0 0 0 2px ${theme.palette.divider}`,
                          flexShrink: 0,
                          zIndex: 1,
                        }}
                      />
                      {!isLast && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 'calc(50% + 8px)',
                            bottom: -20,
                            width: 2,
                            bgcolor: 'divider',
                            borderRadius: 1,
                          }}
                        />
                      )}
                    </Box>

                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      spacing={1}
                      sx={{ minWidth: 0 }}
                    >
                      {event.booking ? (
                        <Box
                          {...(bookingHref
                            ? { component: RouterLink, to: bookingHref }
                            : {})}
                          sx={{
                            minWidth: 0,
                            textDecoration: 'none',
                            color: 'inherit',
                            ...(bookingHref
                              ? {
                                  cursor: 'pointer',
                                  '&:hover .usage-booking-name': { textDecoration: 'underline' },
                                }
                              : {}),
                          }}
                        >
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                            <RoomBookingUserAvatar
                              userProfileImageUrl={event.booking.profileImageUrl}
                              displayName={event.booking.label}
                              size={PASS_IDENTITY_AVATAR_SIZE}
                            />
                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                className="usage-booking-name"
                                variant="subtitle1"
                                fontWeight={700}
                                noWrap
                                sx={{ lineHeight: 1.3 }}
                              >
                                {event.booking.label}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" noWrap>
                                {usageEventLabel(event.eventType)} · {formatDateTime(event.createdAt)}
                              </Typography>
                            </Box>
                          </Stack>
                        </Box>
                      ) : (
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.3 }}>
                            {usageEventLabel(event.eventType)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {formatDateTime(event.createdAt)}
                          </Typography>
                        </Box>
                      )}
                      <Typography
                        variant="subtitle1"
                        fontWeight={800}
                        color={event.quantityDelta < 0 ? 'error.main' : 'success.main'}
                        sx={{ flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}
                      >
                        {passQuantityDeltaLabel(event.quantityDelta, pass.unitType)}
                      </Typography>
                    </Stack>

                    {event.reason && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', gridColumn: 2, mt: 0.5 }}
                      >
                        {event.reason}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Stack>
          )}
        </Box>
      </Box>
    </>
  );
};

export default PassInstanceDetailsView;
